import React from "react";
import {
  Box,
  Card,
  Typography,
  Avatar,
  LinearProgress,
  Grid,
  Fade,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PaymentIcon from "@mui/icons-material/Payment";
import CancelIcon from "@mui/icons-material/Cancel";

const StatCard = ({ title, value, icon, color, total }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;

  return (
    <Card
      elevation={2}
      sx={{
        p: 2.5,
        borderRadius: 3,
        height: "100%",
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={600}
            sx={{ mb: 0.5 }}
          >
            {title}
          </Typography>
          <Typography variant="h3" fontWeight={700} color={color}>
            {value}
          </Typography>
        </Box>
        <Avatar
          sx={{
            bgcolor: color,
            width: 48,
            height: 48,
            boxShadow: `0 4px 12px ${color}40`,
          }}
        >
          {icon}
        </Avatar>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Progress
          </Typography>
          <Typography variant="caption" fontWeight={600} color={color}>
            {percentage.toFixed(0)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: `${color}20`,
            "& .MuiLinearProgress-bar": {
              bgcolor: color,
              borderRadius: 3,
            },
          }}
        />
      </Box>
    </Card>
  );
};

const StatisticsCards = ({ stats, loading }) => {
  return (
    <Fade in={!loading} timeout={1000}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Total Requests"
            value={stats.total}
            icon={<AssignmentIcon />}
            color="#6366f1"
            total={stats.total}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Pending"
            value={stats.pending}
            icon={<HourglassEmptyIcon />}
            color="#ff9800"
            total={stats.total}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Accepted"
            value={stats.accepted}
            icon={<CheckCircleIcon />}
            color="#2196f3"
            total={stats.total}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Verifying"
            value={stats.verifying}
            icon={<PaymentIcon />}
            color="#4caf50"
            total={stats.total}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Rejected"
            value={stats.rejected}
            icon={<CancelIcon />}
            color="#f44336"
            total={stats.total}
          />
        </Grid>
      </Grid>
    </Fade>
  );
};

export default StatisticsCards;